import { useState, useEffect, useCallback, useMemo } from "react";
import { useSeo } from "@/lib/seo";
import { api, adminClient, money } from "@/lib/api";
import { toast } from "sonner";
import { TesterProfile } from "@/components/TesterProfile";
import { JobDetail } from "@/components/JobDetail";
import { CreditsTab } from "@/components/CreditsTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  useSeo({ title: "Admin", noindex: true });
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [creds, setCreds] = useState({ email: "pvsingh@gmail.com", password: "" });
  const [tab, setTab] = useState("jobs");
  const [ov, setOv] = useState(null);
  const [state, setState] = useState({});
  const c = useMemo(() => (token ? adminClient(token) : null), [token]);

  const load = useCallback(async () => {
    if (!c) return;
    try {
      setOv(await c.overview());
      if (tab === "jobs") setState({ jobs: (await c.jobs()).jobs });
      if (tab === "release") setState({ queue: (await c.releaseQueue()).jobs });
      if (tab === "testers") setState(await c.testers());
      if (tab === "payouts") setState({ payouts: (await c.payouts()).payouts });
      if (tab === "events") setState({ events: (await c.events()).events });
      if (tab === "leads") setState({ leads: (await c.leads()).leads });
      if (tab === "brain") setState({ brain: await c.brain() });
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("admin_token"); setToken(""); }
    }
  }, [c, tab]);

  useEffect(() => { load(); }, [load]);

  async function login() {
    try {
      const r = await api.adminLogin(creds);
      localStorage.setItem("admin_token", r.token);
      setToken(r.token);
    } catch (e) { toast.error("Invalid credentials"); }
  }

  if (!token) return (
    <div className="app-main"><div className="wrap" style={{ maxWidth: 420 }}>
      <a className="logo" href="/"><i></i>99tests <span className="tag">admin</span></a>
      <h1 style={{ marginTop: 24 }}>Operator sign-in</h1>
      <div className="panel-soft" style={{ padding: 24, marginTop: 16 }}>
        <div className="field"><label>Email</label><input data-testid="admin-email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} /></div>
        <div className="field"><label>Password</label><input type="password" data-testid="admin-password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} /></div>
        <button className="btn block" data-testid="admin-login-btn" onClick={login}>Sign in</button>
      </div>
    </div></div>
  );

  const tabs = [["jobs", "Jobs"], ["release", "Release queue"], ["testers", "Testers"], ["payouts", "Payouts"], ["credits", "Credits"], ["leads", "Leads"], ["emails", "Emails"], ["analytics", "Analytics"], ["events", "Events"], ["brain", "Brain"]];

  return (
    <>
      <header className="site-header"><div className="wrap"><a className="logo" href="/"><i></i>99tests <span className="tag">admin</span></a>
        <nav><span className="muted small">{ov?.admin_email} · release gate <b>{ov?.release_gate ? "on" : "off"}</b></span>
          <a href="#" onClick={(e) => { e.preventDefault(); localStorage.removeItem("admin_token"); setToken(""); }}>Sign out</a></nav></div></header>
      <main className="app-main"><div className="wrap" style={{ maxWidth: 1240 }}>
        {ov && <div className="kpis" style={{ gridTemplateColumns: "repeat(6,1fr)" }}>
          <div className="kpi"><b>{ov.jobs_active}</b><span>jobs active</span></div>
          <div className="kpi"><b style={{ color: "var(--fail)" }}>{ov.stalled}</b><span>stalled</span></div>
          <div className="kpi"><b style={{ color: "var(--blocked)" }}>{ov.awaiting_release}</b><span>awaiting release</span></div>
          <div className="kpi"><b>{ov.flagged_findings}</b><span>flagged</span></div>
          <div className="kpi"><b>{ov.active_testers}</b><span>active testers</span></div>
          <div className="kpi"><b style={{ color: "var(--fail)" }}>{ov.held_payouts}</b><span>held payouts</span></div>
        </div>}
        <div style={{ display: "flex", gap: 4, borderBottom: "1.5px solid var(--rule-strong)", marginBottom: 24, overflowX: "auto" }}>
          {tabs.map(([t, label]) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
              style={{ background: "none", border: 0, fontWeight: 600, padding: "12px 16px", cursor: "pointer", color: tab === t ? "var(--ink)" : "var(--muted)", borderBottom: `3px solid ${tab === t ? "var(--ink)" : "transparent"}`, marginBottom: -1.5 }}>{label}</button>
          ))}
        </div>

        {tab === "jobs" && <JobsTab jobs={state.jobs || []} c={c} reload={load} />}
        {tab === "release" && <ReleaseTab queue={state.queue || []} c={c} reload={load} />}
        {tab === "testers" && <TestersTab data={state} c={c} reload={load} />}
        {tab === "payouts" && <PayoutsTab payouts={state.payouts || []} c={c} reload={load} />}
        {tab === "emails" && <EmailsTab c={c} />}
        {tab === "credits" && <CreditsTab c={c} />}
        {tab === "analytics" && <AnalyticsTab c={c} />}
        {tab === "leads" && <LeadsTab leads={state.leads || []} c={c} />}
        {tab === "events" && <EventsTab events={state.events || []} />}
        {tab === "brain" && <BrainTab brain={state.brain} c={c} reload={load} />}
      </div></main>
    </>
  );
}

function JobsTab({ jobs, c, reload }) {
  const [open, setOpen] = useState(null);
  const [tester, setTester] = useState(null);
  return (
    <div className="panel-soft" style={{ overflow: "auto" }} data-testid="jobs-tab">
      <table className="tbl"><thead><tr><th>Job</th><th>Client</th><th>Package</th><th>State</th><th>Sessions</th><th>Findings</th><th></th></tr></thead>
        <tbody>{jobs.map((j) => (
          <tr key={j.id} data-testid={`job-row-${j.id}`}><td><button className="btn ghost sm" data-testid={`job-open-${j.id}`} onClick={() => setOpen(j.id)}><b>{j.code}</b></button></td><td>{j.client_email || "—"}</td><td>{j.package}</td>
            <td><span className="pill neutral">{j.state}</span></td><td>{j.sessions_accepted}/{j.sessions_total}</td>
            <td>{j.findings} {j.flagged ? `· ${j.flagged} flagged` : ""}</td>
            <td>{j.state === "stalled" && j.stalled_reason === "unfilled" && <>
              <button className="btn sm" onClick={async () => { await c.redispatch(j.id, true); toast.success("Redispatching wider"); reload(); }}>Redispatch</button>{" "}
              <button className="btn sm ghost" onClick={async () => { const r = await c.refund(j.id); toast.success(`Refunded ${money(r.refunded_minor, "INR")}`); reload(); }}>Refund</button></>}
            </td></tr>
        ))}</tbody></table>
      {jobs.length === 0 && <div className="empty"><b>No jobs yet</b>Jobs appear after checkout.</div>}
      {open && <JobDetail c={c} jobId={open} onClose={() => setOpen(null)} onTester={(id) => setTester(id)} />}
      {tester && <TesterProfile c={c} testerId={tester} onClose={() => setTester(null)} />}
    </div>
  );
}

function ReleaseTab({ queue, c, reload }) {
  return (
    <div data-testid="release-tab">
      {queue.length === 0 && <div className="empty"><b>Nothing awaiting release</b>Verified jobs land here for your gate.</div>}
      {queue.map((j) => (
        <div key={j.id} className="panel-soft" style={{ padding: 22, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}><h3>{j.code} · {j.client_email}</h3><span className="muted small">{j.accepted} accepted · {j.flagged.length} flagged</span></div>
          {j.flagged.map((f) => (
            <div key={f.id} style={{ background: "#fff", border: "1px solid var(--rule)", borderRadius: "var(--r-m)", padding: 16, marginBottom: 12 }}>
              <div><span className="sev" style={{ background: "var(--blocked)" }}>{f.severity_reported}</span> <b style={{ marginLeft: 8 }}>{f.title}</b></div>
              <div className="muted small">{f.case_ref || "no case"} · {f.verify_reason}</div>
              {f.steps && <div className="small" style={{ marginTop: 8 }}><b>Steps</b> {f.steps} · <b>Expected</b> {f.expected} · <b>Actual</b> {f.actual}</div>}
              {f.media?.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }} data-testid={`flagged-media-${f.id}`}>
                  {f.media.map((m) => m.content_type?.startsWith("video/")
                    ? <video key={m.id} src={`${BACKEND_URL}${m.url}`} controls preload="metadata" style={{ maxWidth: 260, maxHeight: 180, borderRadius: 6, border: "1px solid var(--rule)" }} />
                    : <a key={m.id} href={`${BACKEND_URL}${m.url}`} target="_blank" rel="noreferrer"><img src={`${BACKEND_URL}${m.url}`} alt={m.name} style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 6, border: "1px solid var(--rule)" }} /></a>)}
                </div>
              ) : <div className="muted small" style={{ marginTop: 8 }}>No media attached</div>}
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button className="btn sm" data-testid={`appeal-accept-${f.id}`} onClick={async () => { await c.appeal({ finding_id: f.id, decision: "accepted" }); toast.success("Accepted"); reload(); }}>Accept</button>
                <button className="btn sm ghost" onClick={async () => { await c.appeal({ finding_id: f.id, decision: "rejected", reason: "operator reject" }); toast.success("Rejected"); reload(); }}>Reject</button>
              </div>
            </div>
          ))}
          <button className="btn green" data-testid={`release-btn-${j.id}`} style={{ marginTop: 8 }} onClick={async () => { const r = await c.release(j.id); if (r.ok) { toast.success("Report released"); } else toast.error(r.reason); reload(); }}>Release report to client</button>
        </div>
      ))}
    </div>
  );
}

function TestersTab({ data, c, reload }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [react, setReact] = useState(null);
  const [limit, setLimit] = useState("");
  const [q, setQ] = useState("");
  const [stateF, setStateF] = useState("");
  const [page, setPage] = useState(1);
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [hist, setHist] = useState(null);
  useEffect(() => { c.reactivationHistory(30).then(setHist).catch(() => {}); }, [c, data]);
  useEffect(() => { c.reactivation().then(setReact).catch(() => {}); }, [c, data]);
  useEffect(() => {
    const t = setTimeout(() => { c.testers({ q, state: stateF, page, limit: 50 }).then(setList).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [c, q, stateF, page, data]);
  const rows = list?.sample || data.sample || [];

  async function upload(e) {
    const file = e.target.files[0]; if (!file) return;
    e.target.value = "";
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const r = await c.importCsv(fd); setResult(r);
      toast.success(`Imported ${r.inserted} of ${r.rows} rows`); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  }
  async function runBatch() {
    setBusy(true);
    try {
      const r = await c.reactivationRun(limit ? Number(limit) : null);
      toast.success(`${r.invited} activation emails queued`); setReact(r.status); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Batch failed"); }
    finally { setBusy(false); }
  }
  async function exportDevices() {
    try {
      const blob = await c.devicesCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "99tests-tester-devices.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Device export failed"); }
  }
  return (
    <div data-testid="testers-tab">
      <div className="panel-soft" style={{ padding: 22, marginBottom: 16 }}>
        <h3>Pool</h3>
        <div className="kpis" style={{ gridTemplateColumns: "repeat(6,1fr)", marginTop: 14 }}>
          {Object.entries(data.counts || {}).map(([k, v]) => <div key={k} className="kpi"><b>{v}</b><span>{k}</span></div>)}
        </div>
        <label className="btn sm ghost" style={{ marginTop: 12, display: "inline-block", opacity: busy ? 0.6 : 1 }}>{busy ? "Working…" : "Upload tester CSV"}<input type="file" accept=".csv" data-testid="csv-upload" style={{ display: "none" }} onChange={upload} disabled={busy} /></label>
        <button className="btn sm" data-testid="devices-export-btn" style={{ marginTop: 12, marginLeft: 10 }} onClick={exportDevices}>Export devices CSV</button>
        <p className="hint" style={{ marginTop: 8 }}>Accepts the 2024 pool export (LC_Email/LC_Status/real_name/phone) or a plain email,name,phone,city,upi file. Only rows with LC_Status = clean are imported; existing emails are skipped.</p>
        {result && (
          <div className="small" style={{ marginTop: 10, display: "flex", gap: 16, flexWrap: "wrap" }} data-testid="import-result">
            <span><b>{result.rows}</b> rows</span><span><b>{result.inserted}</b> inserted</span><span><b>{result.already_present}</b> already present</span>
            <span><b>{result.batch_duplicates}</b> duplicates in file</span><span><b>{result.rejected}</b> rejected (invalid / not clean)</span>
          </div>
        )}
      </div>
      <div className="panel-soft" style={{ padding: 22, marginBottom: 16 }} data-testid="reactivation-panel">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h3>Reactivation batches</h3>
            <p className="muted small" style={{ margin: "6px 0 0" }}>Ramp {react?.ramp?.join(" → ")} per day. Day {react ? react.day_index + 1 : "–"} · quota <b>{react?.daily_quota ?? "–"}</b> · sent today <b>{react?.sent_today ?? "–"}</b> · remaining <b data-testid="reactivation-remaining">{react?.remaining_today ?? "–"}</b>. Runs nightly too.</p>
            <p className="muted small" style={{ margin: "6px 0 0" }}>Email provider: <b data-testid="email-provider">{react?.email_provider || "–"}</b>{react?.email_from ? <> · from <b>{react.email_from}</b></> : null}</p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="btn sm ghost" data-testid="email-test-btn" disabled={busy} onClick={async () => { setBusy(true); try { const r = await c.emailTest(); r.ok ? toast.success(`Test email sent to ${r.to}`) : toast.error(r.result?.error || "Send failed"); } catch (e) { toast.error("Send failed"); } finally { setBusy(false); } }}>Send test email</button>
            <input type="number" min="1" placeholder="limit" value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="reactivation-limit" style={{ width: 90 }} />
            <button className="btn sm green" data-testid="reactivation-run-btn" disabled={busy || !react || react.remaining_today === 0 || react.imported === 0} onClick={runBatch}>Send reactivation batch</button>
          </div>
        </div>
      </div>
      <div className="panel-soft" style={{ overflow: "auto" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "14px 16px", borderBottom: "1px solid var(--rule)", flexWrap: "wrap" }} data-testid="pool-search">
          <input data-testid="pool-search-input" placeholder="Search name, email, city, phone or UPI…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} style={{ flex: "1 1 260px", width: "auto", minWidth: 240 }} />
          <select data-testid="pool-state-filter" value={stateF} onChange={(e) => { setStateF(e.target.value); setPage(1); }} style={{ width: "auto", flex: "0 0 auto" }}>
            <option value="">All states</option>
            {["imported", "invited", "active", "paused", "bounced", "unsubscribed"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="muted small" data-testid="pool-total">{list ? `${list.total.toLocaleString()} testers` : ""}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn sm ghost" data-testid="pool-prev" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
            <span className="small" style={{ alignSelf: "center" }}>{page} / {list?.pages || 1}</span>
            <button className="btn sm ghost" data-testid="pool-next" disabled={!list || page >= list.pages} onClick={() => setPage(page + 1)}>›</button>
          </div>
        </div>
        <table className="tbl"><thead><tr><th>Name</th><th>Email</th><th>City</th><th>State</th><th>Band</th><th>TesterRank</th><th>TrustRank</th><th>Devices</th><th>UPI</th></tr></thead>
          <tbody>{rows.map((t) => (
            <tr key={t.id} data-testid={`tester-row-${t.id}`} onClick={() => setSelected(t.id)} style={{ cursor: "pointer" }}><td>{t.name || "—"}<div className="muted small">{t.phone || ""}</div></td><td className="small">{t.email}</td><td>{t.city}</td><td><span className={`pill ${["bounced", "unsubscribed"].includes(t.state) ? "fail" : "neutral"}`}>{t.state}</span></td>
              <td>{t.band}</td><td>{t.testerrank}</td><td>{t.trustrank}</td><td>{t.devices}</td><td className="small">{t.upi || "—"}</td></tr>
          ))}</tbody></table>
        {list && rows.length === 0 && <div className="empty"><b>No testers match</b>Try a different search or state.</div>}
      </div>

      <div className="panel-soft" style={{ padding: 22, marginTop: 16 }} data-testid="batch-history">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <h3>Batch history <span className="muted small">last 30 days</span></h3>
          {hist && <span className="muted small">Totals: {hist.totals.sent} sent · {hist.totals.delivered} delivered · {hist.totals.opened} opened · {hist.totals.clicked} clicked · {hist.totals.activated} activated · {hist.totals.bounced} bounced · {hist.totals.unsubscribed} unsubscribed</span>}
        </div>
        {hist && hist.days.length === 0 && <div className="empty"><b>No batches yet</b>Send a reactivation batch and daily counts will appear here.</div>}
        {hist && hist.days.length > 0 && (
          <div style={{ overflow: "auto", marginTop: 12 }}>
            <table className="tbl"><thead><tr><th>Day</th><th className="c">Sent</th><th className="c">Delivered</th><th className="c">Opened</th><th className="c">Clicked</th><th className="c">Activated</th><th className="c">Bounced</th><th className="c">Unsubscribed</th><th className="c">Activation rate</th></tr></thead>
              <tbody>{hist.days.map((r) => (
                <tr key={r.day} data-testid={`batch-day-${r.day}`}><td>{r.day}</td><td className="c num">{r.sent}</td><td className="c num">{r.delivered}</td><td className="c num">{r.opened}</td><td className="c num">{r.clicked}</td>
                  <td className="c num"><b>{r.activated}</b></td><td className="c num" style={{ color: r.bounced ? "var(--fail)" : undefined }}>{r.bounced}</td><td className="c num">{r.unsubscribed}</td>
                  <td className="c num">{r.sent ? `${Math.round((r.activated / r.sent) * 100)}%` : "—"}</td></tr>
              ))}</tbody></table>
          </div>
        )}
        <p className="hint" style={{ marginTop: 10 }}>{hist?.tracking}</p>
      </div>
      {selected && <TesterProfile c={c} testerId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function EmailsTab({ c }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [d, setD] = useState(null);
  const [tester, setTester] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => { c.emails({ q, status, limit: 200 }).then(setD).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [c, q, status]);
  const pill = (s) => ({ failed: "fail", bounced: "fail", complained: "fail", delivered: "", opened: "", clicked: "" }[s] ?? "neutral");
  return (
    <div data-testid="emails-tab">
      {d && (
        <div className="kpis" style={{ gridTemplateColumns: "repeat(5,1fr)", marginBottom: 16 }}>
          <div className="kpi"><b>{d.counts.sent_ok}</b><span>sent ok</span></div>
          <div className="kpi"><b>{d.counts.delivered}</b><span>delivered</span></div>
          <div className="kpi"><b>{d.counts.opened}</b><span>opened / clicked</span></div>
          <div className="kpi"><b style={{ color: d.counts.bounced ? "var(--fail)" : undefined }}>{d.counts.bounced}</b><span>bounced</span></div>
          <div className="kpi"><b style={{ color: d.counts.failed ? "var(--fail)" : undefined }}>{d.counts.failed}</b><span>failed to send</span></div>
        </div>
      )}
      <div className="panel-soft" style={{ overflow: "auto" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "14px 16px", borderBottom: "1px solid var(--rule)", flexWrap: "wrap" }}>
          <input data-testid="emails-search" placeholder="Search recipient or subject…" value={q} onChange={(e) => setQ(e.target.value)} style={{ flex: "1 1 260px", width: "auto", minWidth: 240 }} />
          <select data-testid="emails-status" value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: "auto" }}>
            <option value="">All statuses</option>
            {["sent", "delivered", "opened", "clicked", "bounced", "complained", "failed"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="muted small">{d ? `${d.emails.length} shown · provider ${d.provider}` : ""}</span>
        </div>
        <table className="tbl"><thead><tr><th>When</th><th>To</th><th>Tester</th><th>Subject</th><th>Status</th></tr></thead>
          <tbody>{(d?.emails || []).map((e) => (
            <tr key={e.id} data-testid={`email-row-${e.id}`}><td className="small">{String(e.at).slice(0, 16).replace("T", " ")}</td><td className="small">{e.to}</td>
              <td>{e.tester ? <button className="btn ghost sm" onClick={() => setTester(e.tester.id)}>{e.tester.name || "—"} <span className="muted small">· {e.tester.state}</span></button> : <span className="muted small">—</span>}</td>
              <td>{e.subject}</td>
              <td><span className={`pill ${pill(e.status)}`}>{e.status}</span>{e.error && <div className="muted small" style={{ maxWidth: 320 }}>{String(e.error).slice(0, 120)}</div>}</td></tr>
          ))}</tbody></table>
        {d && d.emails.length === 0 && <div className="empty"><b>No emails match</b>Try a different search or status.</div>}
      </div>
      {tester && <TesterProfile c={c} testerId={tester} onClose={() => setTester(null)} />}
    </div>
  );
}

function PayoutsTab({ payouts, c, reload }) {
  const [led, setLed] = useState(null);
  const [refs, setRefs] = useState({});
  const [busy, setBusy] = useState(false);
  const loadLedger = useCallback(() => c.payoutLedger().then(setLed).catch(() => {}), [c]);
  useEffect(() => { loadLedger(); }, [loadLedger, payouts]);

  async function markPaid(r) {
    const reference = (refs[r.tester_id] || "").trim();
    if (reference.length < 4) { toast.error("Enter the UPI transaction reference (UTR)"); return; }
    setBusy(true);
    try {
      const res = await c.payoutMarkPaid({ tester_id: r.tester_id, reference });
      toast.success(`Marked ${money(res.amount, "INR")} paid to ${r.name || r.email}`);
      setRefs({ ...refs, [r.tester_id]: "" }); loadLedger(); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark paid"); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="payouts-tab">
      <div className="panel-soft" style={{ padding: 22, marginBottom: 16 }} data-testid="payout-ledger">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h3>UPI payouts — manual</h3>
            <p className="muted small" style={{ margin: "6px 0 0" }}>Pay weekly, or when a tester reaches {led ? money(led.min_paise, "INR") : "₹500"}. Download the CSV, pay from your UPI app, then paste each UTR and mark paid. A digest lands in your inbox every Monday.</p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <a className="btn sm" data-testid="payout-export-due" href={c.payoutExportUrl(true)}>Download due CSV</a>
            <a className="btn sm ghost" data-testid="payout-export-all" href={c.payoutExportUrl(false)}>All pending CSV</a>
            <button className="btn sm ghost" data-testid="payout-digest-btn" disabled={busy} onClick={async () => { setBusy(true); try { const r = await c.payoutDigest(); r.ok ? toast.success(`Digest sent to ${r.to}`) : toast.error("Send failed"); } finally { setBusy(false); } }}>Email me the digest now</button>
          </div>
        </div>
        {led && (
          <div className="kpis" style={{ gridTemplateColumns: "repeat(5,1fr)", marginTop: 16 }}>
            <div className="kpi"><b data-testid="ledger-due-total">{money(led.due_total, "INR")}</b><span>due now · {led.due_testers} testers</span></div>
            <div className="kpi"><b>{money(led.pending_total, "INR")}</b><span>all pending · {led.pending_testers} testers</span></div>
            <div className="kpi"><b>{money(led.paid_last_7d, "INR")}</b><span>paid last 7 days · {led.paid_count_7d}</span></div>
            <div className="kpi"><b style={{ color: led.held ? "var(--fail)" : undefined }}>{led.held}</b><span>held · no valid UPI</span></div>
            <div className="kpi"><b>{led.mode}</b><span>payout mode</span></div>
          </div>
        )}
      </div>

      <div className="panel-soft" style={{ overflow: "auto", marginBottom: 16 }}>
        <table className="tbl"><thead><tr><th>Tester</th><th>UPI ID</th><th className="c">Balance</th><th>Sessions</th><th>Oldest</th><th>UTR / reference</th><th></th></tr></thead>
          <tbody>{(led?.rows || []).map((r) => (
            <tr key={r.tester_id} data-testid={`ledger-row-${r.tester_id}`} style={{ background: r.due ? "rgba(14,124,75,.06)" : undefined }}>
              <td>{r.due && <span className="pill" style={{ marginRight: 6 }}>Due</span>}{r.name || r.email}<div className="muted small">{r.city}{r.phone ? ` · ${r.phone}` : ""}</div></td>
              <td>{r.vpa || <span className="pill fail">no UPI</span>}</td>
              <td className="c num"><b>{money(r.amount, "INR")}</b></td>
              <td>{r.count}</td>
              <td className="muted small">{r.oldest?.slice(0, 10)}</td>
              <td><input data-testid={`utr-${r.tester_id}`} placeholder="UPI ref no." value={refs[r.tester_id] || ""} onChange={(e) => setRefs({ ...refs, [r.tester_id]: e.target.value })} style={{ width: 160 }} /></td>
              <td><button className="btn sm green" data-testid={`mark-paid-${r.tester_id}`} disabled={busy || !r.vpa} onClick={() => markPaid(r)}>Mark paid</button></td>
            </tr>
          ))}</tbody></table>
        {led && led.rows.length === 0 && <div className="empty"><b>Nothing pending</b>Balances appear here when a report is delivered.</div>}
      </div>

      <details>
        <summary className="muted small" style={{ cursor: "pointer", marginBottom: 8 }}>All payout records ({payouts.length})</summary>
        <div className="panel-soft" style={{ overflow: "auto" }}>
          <table className="tbl"><thead><tr><th>Tester</th><th>Job</th><th className="c">Amount</th><th>UPI</th><th>State</th><th></th></tr></thead>
            <tbody>{payouts.map((p) => (
              <tr key={p.id}><td>{p.tester} · {p.city}</td><td>{p.job_code}</td><td className="c num">{money(p.amount, "INR")}</td>
                <td>{p.vpa || "—"}</td><td><span className={`pill ${["held", "failed"].includes(p.state) ? "fail" : ""}`}>{p.state}</span></td>
                <td>{["held", "failed"].includes(p.state) && <button className="btn sm ghost" onClick={async () => { await c.payoutRetry(p.id); toast.success("Retrying"); reload(); }}>Retry</button>}</td></tr>
            ))}</tbody></table>
          {payouts.length === 0 && <div className="empty"><b>No payouts yet</b>Payouts are created when a report is delivered.</div>}
        </div>
      </details>
    </div>
  );
}

function AnalyticsTab({ c }) {
  const [days, setDays] = useState(14);
  const [d, setD] = useState(null);
  useEffect(() => { let on = true; setD(null); c.analytics(days).then((x) => on && setD(x)).catch(() => {}); return () => { on = false; }; }, [c, days]);
  if (!d) return <p data-testid="analytics-loading">Loading…</p>;
  const maxV = Math.max(1, ...d.daily.map((x) => x.views));
  const paidMap = Object.fromEntries((d.paid_by_landing || []).map((p) => [p.landing, p]));
  const landingRows = (d.by_landing || []).map((l) => ({ ...l, paid: paidMap[l.path] || { jobs: 0, inr_minor: 0, usd_minor: 0 } }));
  const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);
  return (
    <div data-testid="analytics-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div className="kpis" style={{ gridTemplateColumns: "repeat(2,minmax(150px,1fr))", flex: "1 1 320px", margin: 0 }}>
          <div className="kpi"><b data-testid="analytics-visitors">{d.unique_visitors}</b><span>unique visitors</span></div>
          <div className="kpi"><b data-testid="analytics-views">{d.total_views}</b><span>page views</span></div>
        </div>
        <select data-testid="analytics-days" value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ width: "auto" }}>
          <option value={7}>Last 7 days</option><option value={14}>Last 14 days</option><option value={30}>Last 30 days</option>
        </select>
      </div>

      <div className="panel-soft" style={{ padding: 18, marginBottom: 16 }}>
        <h4 style={{ margin: "0 0 14px" }}>Visitors per day</h4>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 120 }} data-testid="analytics-chart">
          {d.daily.map((x) => (
            <div key={x.date} title={`${x.date}: ${x.views} views`} style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}>
              <div style={{ height: `${(x.views / maxV) * 100}%`, minHeight: x.views ? 3 : 0, background: "#2563eb", borderRadius: "3px 3px 0 0", transition: "height .3s" }}></div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
          <span className="muted small">{d.daily[0]?.date}</span><span className="muted small">{d.daily[d.daily.length - 1]?.date}</span>
        </div>
      </div>

      <div className="panel-soft" style={{ padding: 0, overflow: "auto", marginBottom: 16 }}>
        <div style={{ padding: "16px 18px 0" }}><h4 style={{ margin: 0 }}>Landing pages</h4><p className="muted small" style={{ margin: "4px 0 0" }}>Views, visitors and paid jobs attributed to the first page each visitor landed on.</p></div>
        <table className="tbl" data-testid="analytics-landing"><thead><tr><th>Page</th><th className="c">Views</th><th className="c">Visitors</th><th className="c">Paid jobs</th><th className="c">Revenue</th></tr></thead>
          <tbody>{landingRows.map((r) => (
            <tr key={r.path}><td><b>{r.label}</b><div className="muted small">{r.path}</div></td>
              <td className="c">{r.views}</td><td className="c">{r.visitors}</td>
              <td className="c"><b>{r.paid.jobs}</b></td>
              <td className="c small">{r.paid.inr_minor ? money(r.paid.inr_minor, "INR") : ""}{r.paid.inr_minor && r.paid.usd_minor ? " · " : ""}{r.paid.usd_minor ? money(r.paid.usd_minor, "USD") : ""}{!r.paid.inr_minor && !r.paid.usd_minor ? "—" : ""}</td></tr>
          ))}{landingRows.length === 0 && <tr><td colSpan={5} className="muted small">No page views yet in this window.</td></tr>}</tbody></table>
      </div>

      <div className="panel-soft" style={{ padding: 0, overflow: "auto", marginBottom: 16 }}>
        <div style={{ padding: "16px 18px 0" }}><h4 style={{ margin: 0 }}>Conversion funnel</h4><p className="muted small" style={{ margin: "4px 0 0" }}>Landing visitors → checkouts started → paid, by first-touch page. Rates show drop-off at each step.</p></div>
        <table className="tbl" data-testid="analytics-funnel"><thead><tr><th>Page</th><th className="c">Visitors</th><th className="c">Checkouts started</th><th className="c">Paid</th><th className="c">Visitor→paid</th></tr></thead>
          <tbody>{(d.funnel || []).map((r) => (
            <tr key={r.landing}><td><b>{r.label}</b><div className="muted small">{r.landing}</div></td>
              <td className="c">{r.visitors}</td>
              <td className="c">{r.started}<div className="muted small">{pct(r.started, r.visitors)}% of visitors</div>
                {(r.started_clients || []).length > 0 && <div className="small" style={{ marginTop: 4, textAlign: "left" }} data-testid={`funnel-started-${r.landing}`}>{r.started_clients.map((e) => <div key={e} style={{ whiteSpace: "nowrap" }}>{e}</div>)}{r.started_more > 0 && <div className="muted">+{r.started_more} more</div>}</div>}</td>
              <td className="c"><b>{r.paid}</b><div className="muted small">{pct(r.paid, r.started)}% of started</div>
                {(r.paid_clients || []).length > 0 && <div className="small" style={{ marginTop: 4, textAlign: "left" }} data-testid={`funnel-clients-${r.landing}`}>{r.paid_clients.map((e) => <div key={e} style={{ whiteSpace: "nowrap" }}>{e}</div>)}{r.paid_more > 0 && <div className="muted">+{r.paid_more} more</div>}</div>}</td>
              <td className="c"><b style={{ color: r.paid ? "var(--green)" : "var(--muted)" }}>{pct(r.paid, r.visitors)}%</b></td></tr>
          ))}{(d.funnel || []).length === 0 && <tr><td colSpan={5} className="muted small">No funnel activity yet in this window.</td></tr>}</tbody></table>
      </div>

      <div className="panel-soft" style={{ padding: 0, overflow: "auto", marginBottom: 16 }}>
        <div style={{ padding: "16px 18px 0" }}><h4 style={{ margin: 0 }}>Traffic sources</h4></div>
        <table className="tbl" data-testid="analytics-sources"><thead><tr><th>Source</th><th className="c">Views</th></tr></thead>
          <tbody>{(d.by_source || []).map((s) => (
            <tr key={s.source}><td>{s.source}</td><td className="c">{s.views}</td></tr>
          ))}{(d.by_source || []).length === 0 && <tr><td colSpan={2} className="muted small">No traffic yet.</td></tr>}</tbody></table>
      </div>

      <div className="panel-soft" style={{ padding: 0, overflow: "auto" }}>
        <div style={{ padding: "16px 18px 0" }}><h4 style={{ margin: 0 }}>Campaigns (UTM)</h4><p className="muted small" style={{ margin: "4px 0 0" }}>Tagged visits by utm_campaign / utm_medium / utm_source — for measuring specific ads or emails.</p></div>
        <table className="tbl" data-testid="analytics-campaigns"><thead><tr><th>Campaign</th><th>Medium</th><th>Source</th><th className="c">Views</th><th className="c">Visitors</th><th className="c">Paid</th></tr></thead>
          <tbody>{(d.by_campaign || []).map((r, i) => (
            <tr key={i}><td><b>{r.campaign}</b></td><td>{r.medium}</td><td>{r.source}</td>
              <td className="c">{r.views}</td><td className="c">{r.visitors}</td><td className="c"><b>{r.paid}</b></td></tr>
          ))}{(d.by_campaign || []).length === 0 && <tr><td colSpan={6} className="muted small">No UTM-tagged traffic yet. Add ?utm_campaign=&utm_medium=&utm_source= to your links.</td></tr>}</tbody></table>
      </div>
    </div>
  );
}

function EventsTab({ events }) {
  return (
    <div className="panel-soft" style={{ overflow: "auto" }} data-testid="events-tab">
      <table className="tbl"><thead><tr><th>Time</th><th>Type</th><th>Actor</th><th>From→To</th></tr></thead>
        <tbody>{events.map((e) => (
          <tr key={e.id}><td className="small">{new Date(e.at).toLocaleString()}</td><td><b>{e.type}</b></td><td>{e.actor}</td>
            <td className="small">{e.from_state ? `${e.from_state} → ${e.to_state}` : ""}</td></tr>
        ))}</tbody></table>
    </div>
  );
}

function BrainTab({ brain, c, reload }) {
  if (!brain) return <p>Loading…</p>;
  return (
    <div data-testid="brain-tab">
      <div className="panel-soft" style={{ padding: 22, marginBottom: 16 }}>
        <h3>Brain modules</h3>
        <p className="muted small">Only the operator flips modes. Shadow modules are logged, never applied.</p>
        <table className="tbl" style={{ marginTop: 12 }}><thead><tr><th>Module</th><th>Mode</th><th>Launch gate</th><th></th></tr></thead>
          <tbody>{Object.entries(brain.modules).map(([mod, cfg]) => (
            <tr key={mod}><td><b>{mod}</b></td><td><span className={`pill ${cfg.mode === "live" ? "" : "neutral"}`}>{cfg.mode}</span></td>
              <td className="small muted">{brain.launch_gates?.[mod] || "—"}</td>
              <td><select value={cfg.mode} data-testid={`brain-mode-${mod}`} onChange={async (e) => { await c.brainFlip(mod, e.target.value); toast.success(`${mod} → ${e.target.value}`); reload(); }}>
                <option value="off">off</option><option value="shadow">shadow</option><option value="live">live</option></select></td></tr>
          ))}</tbody></table>
      </div>
      <div className="panel-soft" style={{ padding: 22, marginBottom: 16 }}>
        <h3>TesterRank band distribution</h3>
        <div className="kpis" style={{ gridTemplateColumns: "repeat(5,1fr)", marginTop: 14 }}>
          {Object.entries(brain.band_distribution || {}).map(([b, n]) => <div key={b} className="kpi"><b>{n}</b><span>{b}</span></div>)}
        </div>
        <p className="muted small" style={{ marginTop: 12 }}>Last recompute: {brain.last_recompute ? new Date(brain.last_recompute).toLocaleString() : "pending"}</p>
      </div>
      <div className="panel-soft" style={{ padding: 22, overflow: "auto" }}>
        <h3>Recent decisions (DecisionTrace)</h3>
        <table className="tbl" style={{ marginTop: 12 }}><thead><tr><th>Time</th><th>Type</th><th>Payload</th></tr></thead>
          <tbody>{(brain.recent_decisions || []).slice(0, 20).map((d) => (
            <tr key={d.id}><td className="small">{new Date(d.at).toLocaleString()}</td><td><b>{d.type}</b></td><td className="small">{JSON.stringify(d.payload)}</td></tr>
          ))}</tbody></table>
      </div>
    </div>
  );
}

function LeadsTab({ leads, c }) {
  const [ab, setAb] = useState(null);
  useEffect(() => { c.leadsAb().then((r) => setAb(r.variants)).catch(() => {}); }, [c]);
  async function exportCsv() {
    try {
      const blob = await c.leadsCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "99tests-leads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  }
  const stageLabel = (s) => (["sent", "+3d", "+7d"][s] ?? s);
  return (
    <div data-testid="leads-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Leads <span className="muted">({leads.length})</span></h3>
        <button className="btn sm" data-testid="leads-export-btn" onClick={exportCsv}>Export CSV</button>
      </div>
      {ab && (
        <div data-testid="ab-gate-panel" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          {["A", "B"].map((v) => {
            const d = ab[v] || { impressions: 0, conversions: 0, rate: 0 };
            const label = v === "A" ? "A · control copy" : "B · stronger promise";
            const best = ab.A && ab.B && (ab[v].rate >= ab[v === "A" ? "B" : "A"].rate) && ab[v].impressions > 0;
            return (
              <div key={v} className="panel-soft" data-testid={`ab-variant-${v}`} style={{ padding: "14px 16px", borderLeft: `3px solid ${best ? "var(--green,#0E7C4B)" : "var(--rule-strong)"}` }}>
                <div className="muted small" style={{ marginBottom: 4 }}>Gate modal · variant {label}</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                  <b style={{ fontSize: 26 }} data-testid={`ab-rate-${v}`}>{d.rate}%</b>
                  <span className="muted small">{d.conversions} email{d.conversions === 1 ? "" : "s"} · {d.impressions} shown</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead><tr style={{ textAlign: "left", borderBottom: "2px solid var(--rule-strong)" }}>
            <th style={{ padding: "8px 10px" }}>Email</th><th style={{ padding: "8px 10px" }}>Company</th>
            <th style={{ padding: "8px 10px" }}>Captured</th><th style={{ padding: "8px 10px" }}>Follow-up</th>
            <th style={{ padding: "8px 10px" }}>Campaign</th><th style={{ padding: "8px 10px" }}>Status</th>
          </tr></thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} style={{ borderBottom: "1px solid var(--rule)" }}>
                <td style={{ padding: "8px 10px" }}>{l.email}</td>
                <td style={{ padding: "8px 10px" }}>{l.company || "—"}</td>
                <td style={{ padding: "8px 10px" }}>{(l.created_at || "").slice(0, 10)}</td>
                <td style={{ padding: "8px 10px" }}>{stageLabel(l.stage)}</td>
                <td style={{ padding: "8px 10px" }}>{(l.attribution && (l.attribution.campaign || l.attribution.source)) || "—"}</td>
                <td style={{ padding: "8px 10px" }}>{l.paid ? "paid" : l.unsubscribed ? "unsubscribed" : "active"}</td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={6} className="muted" style={{ padding: "16px 10px" }}>No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

